import React from "react";
import { useCart } from "../@sdk/react";

type ProductResult = {
  available_quantity?: number | null;
  category?: string | null;
  currency?: string | null;
  description?: string | null;
  family?: string | null;
  image_url?: string | null;
  is_available?: boolean | null;
  name: string;
  price?: string | null;
  role?: string | null;
  sku?: string | null;
  tag?: string | null;
  url: string;
  variant_id?: string | null;
};

type Message = {
  text: string;
  type: "bot" | "user";
};

type AiResponse = {
  intent?: string;
  ok?: boolean;
  reply?: string;
  results?: ProductResult[];
  suggestions?: string[];
};

type FollowupContext = {
  baseQuery: string;
  brand: string;
  family: string;
  useCase: string;
};

type ResultHistoryEntry = {
  followupContext: FollowupContext;
  id: string;
  intent: string;
  query: string;
  results: ProductResult[];
  suggestions: string[];
};

type PostAddActions = {
  itemCount: number;
  productName: string;
  quantity: number;
  show: boolean;
};

type SetupSummary = {
  currency: string;
  itemCount: number;
  total: string;
};

type SetupCartItem = {
  name: string;
  variantId: string;
};

type AssistantComparison = {
  leftName: string;
  rightName: string;
  rows: Array<{
    label: string;
    left: string;
    right: string;
  }>;
};

type AssistantFlowType =
  | ""
  | "category"
  | "compatibility"
  | "maintenance"
  | "setup";
type AssistantCategory =
  | ""
  | "blade"
  | "clipper"
  | "shaver"
  | "supply"
  | "trimmer";
type AssistantPriceMode = "" | "lower";
type ProductRole = "replacement-part" | "support-item" | "tool" | "unknown";

type AssistantState = {
  brand: string;
  currentCategory: AssistantCategory;
  flowType: AssistantFlowType;
  priceMode: AssistantPriceMode;
  useCase: string;
};

type AssistantSessionSnapshot = {
  assistantState: AssistantState;
  followupContext: FollowupContext;
  lastIntent: string;
  lastQuery: string;
  messages: Message[];
  resultHistory: ResultHistoryEntry[];
  results: ProductResult[];
  savedAt: number;
  suggestions: string[];
};

const API_URL = "https://api.egtsupplies.com/api/ai/chat/";
const CHECKOUT_URL = "/checkout/";
const ASSISTANT_REQUEST_TIMEOUT_MS = 15000;
const ASSISTANT_SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const ASSISTANT_SESSION_STORAGE_KEY = "egt-ai-assistant-session-v2";
const DEFAULT_SUGGESTIONS = [
  "Clippers",
  "Trimmers",
  "Fade setup",
  "Blade compatibility",
  "Barber supplies",
  "Order help",
];
const KNOWN_BRANDS = [
  "wahl",
  "andis",
  "babyliss",
  "oster",
  "gamma",
  "stylecraft",
  "jrl",
];
const SUPPORT_QUERY_HINTS = [
  "blade care",
  "clipper oil",
  "cool care",
  "blade spray",
  "maintenance",
  "cleaning spray",
  "blade cleaner",
  "oil",
  "coolant",
  "lubricant",
  "replacement blade",
  "replacement foil",
  "accessory",
  "accessories",
  "clip comb",
  "clip combs",
];
const EMPTY_CONTEXT: FollowupContext = {
  baseQuery: "",
  brand: "",
  family: "",
  useCase: "",
};

const EMPTY_ASSISTANT_STATE: AssistantState = {
  brand: "",
  currentCategory: "",
  flowType: "",
  priceMode: "",
  useCase: "",
};

const normalizeText = (value?: string | null) =>
  (value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
const parsePrice = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const numeric = String(value).replace(/[^0-9.]/g, "");
  const parsed = Number(numeric);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  return parsed;
};

const formatMoney = (value?: string | null, currency?: string | null) => {
  const parsed = parsePrice(value);

  if (parsed === null) {
    return value || "Price unavailable";
  }

  try {
    return new Intl.NumberFormat("en-US", {
      currency: currency || "USD",
      maximumFractionDigits: 2,
      style: "currency",
    }).format(parsed);
  } catch (_error) {
    return `$${parsed.toFixed(2)}`;
  }
};

export const getAssistantSetupSummary = (
  items: ProductResult[]
): SetupSummary | null => {
  if (items.length < 2) {
    return null;
  }

  const prices = items.map(item => parsePrice(item.price));
  if (prices.some(price => price === null)) {
    return null;
  }

  const currencies = Array.from(
    new Set(items.map(item => item.currency || "USD"))
  );
  if (currencies.length !== 1) {
    return null;
  }

  const total = (prices as number[]).reduce((sum, price) => sum + price, 0);

  return {
    currency: currencies[0],
    itemCount: items.length,
    total: total.toFixed(2),
  };
};

export const getAssistantSetupCartItems = (
  items: ProductResult[]
): SetupCartItem[] | null => {
  if (items.length < 2) {
    return null;
  }

  const variantIds = new Set<string>();
  const cartItems: SetupCartItem[] = [];

  for (const item of items) {
    const variantId = (item.variant_id || "").trim();

    if (
      !isAssistantResultAvailable(item) ||
      !variantId ||
      variantIds.has(variantId)
    ) {
      return null;
    }

    variantIds.add(variantId);
    cartItems.push({
      name: item.name,
      variantId,
    });
  }

  return cartItems;
};

export const isAssistantResultAvailable = (item: ProductResult) => {
  if (!item.variant_id || item.is_available === false) {
    return false;
  }

  if (
    typeof item.available_quantity === "number" &&
    item.available_quantity <= 0
  ) {
    return false;
  }

  return true;
};

export const canAddAssistantQuantity = (
  item: ProductResult,
  quantity: number
) => {
  if (!Number.isInteger(quantity) || quantity <= 0) {
    return false;
  }
  if (!isAssistantResultAvailable(item)) {
    return false;
  }
  if (typeof item.available_quantity === "number") {
    return item.available_quantity >= quantity;
  }

  return true;
};

export const getAssistantAvailabilityLabel = (item: ProductResult) => {
  if (!isAssistantResultAvailable(item)) {
    return "Out of stock";
  }

  if (
    typeof item.available_quantity === "number" &&
    item.available_quantity <= 5
  ) {
    return `Only ${item.available_quantity} left`;
  }

  return "In stock";
};

const inferBrandFromText = (value: string) => {
  const lower = normalizeText(value);
  return KNOWN_BRANDS.find(brand => lower.includes(brand)) || "";
};

const productText = (product: ProductResult) =>
  normalizeText(
    [product.name, product.sku, product.tag].filter(Boolean).join(" ")
  );

const isReplacementPartText = (value: string) => {
  const text = normalizeText(value);

  if (
    text.includes("blade assembly") ||
    text.includes("cutter assembly") ||
    text.includes("foil assembly") ||
    text.includes("foil head") ||
    text.includes("replacement cutter") ||
    text.includes("replacement foil") ||
    text.includes("replacement head") ||
    text.includes("replacement screen") ||
    text.includes("replacement blade") ||
    text.includes("blade only")
  ) {
    return true;
  }

  return (
    text.includes("replacement") &&
    (text.includes("blade") ||
      text.includes("cutter") ||
      text.includes("foil") ||
      text.includes("head") ||
      text.includes("screen"))
  );
};

const isSupportProductText = (value: string) => {
  const text = normalizeText(value);

  return (
    text.includes("accessory") ||
    text.includes("barbicide") ||
    text.includes("blade care") ||
    text.includes("blade cleaner") ||
    text.includes("blade oil") ||
    text.includes("blade spray") ||
    text.includes("cape") ||
    text.includes("charging base") ||
    text.includes("charging stand") ||
    text.includes("cleaning spray") ||
    text.includes("clip comb") ||
    text.includes("clipper oil") ||
    text.includes("comb set") ||
    text.includes("cool care") ||
    text.includes("coolant") ||
    text.includes("disinfect") ||
    text.includes("guard set") ||
    text.includes("guide comb") ||
    text.includes("lubricant") ||
    text.includes("neck strip") ||
    text.includes("sanitiz") ||
    text.includes("shop supply")
  );
};

const inferFamily = (value: string) => {
  const text = normalizeText(value);

  if (isReplacementPartText(text)) {
    return "blade";
  }
  if (isSupportProductText(text)) {
    return "supply";
  }
  if (text.includes("shaver")) {
    return "shaver";
  }
  if (
    text.includes("trimmer") ||
    text.includes("liner") ||
    text.includes("detailer") ||
    text.includes("outliner")
  ) {
    return "trimmer";
  }
  if (text.includes("clipper")) {
    return "clipper";
  }
  if (
    text.includes("blade") ||
    text.includes("cutter") ||
    text.includes("foil") ||
    text.includes("screen")
  ) {
    return "blade";
  }
  if (
    text.includes("barber pencil") ||
    text.includes("blade brush") ||
    text.includes("hair spray") ||
    text.includes("hairspray") ||
    text.includes("neck strip") ||
    text.includes("pomade") ||
    text.includes("sanitation") ||
    text.includes("styling product") ||
    text.includes("cool care") ||
    text.includes("barbicide") ||
    text.includes("oil") ||
    text.includes("cape") ||
    text.includes("comb") ||
    text.includes("supply")
  ) {
    return "supply";
  }

  return "";
};

const canonicalAssistantFamily = (value?: string | null) => {
  const family = normalizeText(value);

  if (
    family === "accessory" ||
    family === "barber supplies" ||
    family === "combs and guards" ||
    family === "sanitation" ||
    family === "styling products" ||
    family === "support" ||
    family === "supply"
  ) {
    return "supply";
  }

  return family;
};

const getAssistantResultFamily = (product: ProductResult) =>
  canonicalAssistantFamily(product.family) || inferFamily(productText(product));

const inferProductRole = (product: ProductResult): ProductRole => {
  const explicitRole = normalizeText(product.role);
  if (explicitRole === "replacement") {
    return "replacement-part";
  }
  if (explicitRole === "support") {
    return "support-item";
  }
  if (explicitRole === "tool") {
    return "tool";
  }

  const family = getAssistantResultFamily(product);

  if (family === "blade") {
    return "replacement-part";
  }
  if (family === "supply") {
    return "support-item";
  }
  if (family === "clipper" || family === "shaver" || family === "trimmer") {
    return "tool";
  }

  return "unknown";
};

export const getAssistantProductClassification = (
  name: string,
  sku = "",
  tag = ""
) => {
  const product: ProductResult = {
    name,
    sku,
    tag,
    url: "#",
  };

  return {
    family: getAssistantResultFamily(product),
    role: inferProductRole(product),
  };
};

const inferResultsFamily = (results: ProductResult[]) => {
  if (!results.length) {
    return "";
  }

  return getAssistantResultFamily(results[0]);
};

const inferUseCaseFromText = (value: string) => {
  const lower = normalizeText(value);

  if (lower.includes("fade")) {
    return "fades";
  }
  if (lower.includes("lineup") || lower.includes("line up")) {
    return "lineups";
  }
  if (lower.includes("beard")) {
    return "beard";
  }
  if (lower.includes("starter") || lower.includes("beginner")) {
    return "starter";
  }
  if (lower.includes("blade")) {
    return "blade";
  }
  if (lower.includes("care") || lower.includes("maintenance")) {
    return "maintenance";
  }

  return "";
};

const isMaintenanceQuery = (text: string) => {
  const q = normalizeText(text);
  return SUPPORT_QUERY_HINTS.some(hint => q.includes(hint));
};

const isCompatibilityQuery = (text: string) => {
  const q = normalizeText(text);

  if (
    q.includes("compatib") ||
    q.includes("which blade fits") ||
    q.includes("what blade fits") ||
    q.includes("does this fit") ||
    q.includes("will this fit") ||
    q.includes("fits my") ||
    q.includes("fit my")
  ) {
    return true;
  }

  return false;
};

const isSetupQuery = (query: string) => {
  const q = normalizeText(query);
  return (
    q.includes("setup") ||
    q.includes("starter") ||
    q.includes("beginner") ||
    q.includes("bundle") ||
    q.includes("kit")
  );
};

const inferCategoryFromQuery = (value: string): AssistantCategory => {
  const q = normalizeText(value);

  if (q.includes("foil shaver") || q.includes("shaver")) {
    return "shaver";
  }
  if (
    q.includes("trimmer") ||
    q.includes("liner") ||
    q.includes("outliner") ||
    q.includes("detailer")
  ) {
    return "trimmer";
  }
  if (q.includes("clipper")) {
    return "clipper";
  }
  if (
    q.includes("replacement foil") ||
    q.includes("replacement blade") ||
    q.includes("blade")
  ) {
    return "blade";
  }
  if (
    q.includes("barber pencil") ||
    q.includes("brush") ||
    q.includes("comb") ||
    q.includes("disposable") ||
    q.includes("guard") ||
    q.includes("hair spray") ||
    q.includes("hairspray") ||
    q.includes("neck strip") ||
    q.includes("pomade") ||
    q.includes("sanitation") ||
    q.includes("styling product") ||
    q.includes("supply") ||
    q.includes("care") ||
    q.includes("oil") ||
    q.includes("cool care")
  ) {
    return "supply";
  }

  return "";
};

const isContextualFollowupQuery = (value: string) => {
  const normalized = normalizeText(value);
  const weakFollowups = [
    "blade care",
    "blade spray",
    "cheaper",
    "clipper oil",
    "cool care",
    "corded",
    "corded only",
    "cordless",
    "cordless only",
    "lower price",
    "maintenance",
    "need blade care",
    "replacement blade",
    "replacement blades",
    "replacement foil",
  ];

  return (
    weakFollowups.includes(normalized) || normalized.startsWith("what about ")
  );
};

export const deriveAssistantState = (
  query: string,
  previous: AssistantState,
  nextContext: FollowupContext,
  intent?: string
): AssistantState => {
  const normalized = normalizeText(query);
  if (intent === "greeting" || intent === "order_help") {
    return EMPTY_ASSISTANT_STATE;
  }

  const contextualFollowup = isContextualFollowupQuery(query);
  const explicitCategory = inferCategoryFromQuery(query);
  let flowType: AssistantFlowType = previous.flowType;
  let currentCategory: AssistantCategory =
    explicitCategory ||
    (contextualFollowup ? previous.currentCategory : "") ||
    (canonicalAssistantFamily(nextContext.family) as AssistantCategory) ||
    "";
  let priceMode: AssistantPriceMode = previous.priceMode;
  const brand =
    nextContext.brand || (contextualFollowup ? previous.brand : "") || "";
  const useCase =
    nextContext.useCase || (contextualFollowup ? previous.useCase : "") || "";

  if (isSetupQuery(query)) {
    flowType = "setup";
  } else if (explicitCategory && !contextualFollowup) {
    flowType = "category";
  }
  if (
    normalized === "lower price" ||
    normalized === "cheaper" ||
    normalized.startsWith("lower price ")
  ) {
    priceMode = "lower";
  }
  if (normalized.startsWith("what about ")) {
    flowType = previous.flowType || "category";
  }
  if (isCompatibilityQuery(query) || intent === "compatibility_search") {
    flowType = "compatibility";
  } else if (isMaintenanceQuery(query)) {
    flowType = "maintenance";
  }
  if (!flowType && currentCategory) {
    flowType = "category";
  }
  if (flowType === "maintenance" && !currentCategory) {
    currentCategory = previous.currentCategory;
  }

  return {
    brand,
    currentCategory,
    flowType,
    priceMode,
    useCase,
  };
};

const getModeBadgeFromState = (state: AssistantState) => {
  if (state.flowType === "maintenance") {
    return "Maintenance help";
  }
  if (state.flowType === "compatibility") {
    return "Compatibility";
  }
  if (state.flowType === "setup") {
    return "Guided setup";
  }
  if (state.flowType === "category") {
    return "Product search";
  }

  return "";
};

const getScopedSuggestions = (state: AssistantState) => {
  if (state.flowType === "maintenance") {
    if (state.currentCategory === "clipper") {
      return [
        "Cool care",
        "Clipper oil",
        "Replacement blades",
        "Clipper accessories",
        "Show related products",
        "Back to shopping",
      ];
    }
    if (state.currentCategory === "trimmer") {
      return [
        "Cool care",
        "Clipper oil",
        "Replacement blades",
        "Trimmer accessories",
        "Show related products",
        "Back to shopping",
      ];
    }
    if (state.currentCategory === "shaver") {
      return [
        "Replacement foil",
        "Cool care",
        "Shaver accessories",
        "Show related products",
        "Back to shopping",
      ];
    }

    return [
      "Cool care",
      "Clipper oil",
      "Replacement blades",
      "Replacement foil",
      "Show related products",
      "Back to shopping",
    ];
  }

  if (state.flowType === "setup") {
    if (state.currentCategory === "trimmer") {
      return [
        "Trimmers",
        "Lower price",
        "Compare top 2",
        "What about Wahl",
        "Replacement blades",
        "Need blade care",
        "Show related products",
      ];
    }
    if (state.currentCategory === "shaver") {
      return [
        "Shavers",
        "Lower price",
        "Compare top 2",
        "What about Wahl",
        "Replacement foil",
        "Need blade care",
        "Show related products",
      ];
    }
    if (state.currentCategory === "clipper") {
      return [
        "Clippers",
        "Lower price",
        "Compare top 2",
        "What about Wahl",
        "Clipper accessories",
        "Need blade care",
        "Show related products",
      ];
    }
    if (state.currentCategory === "supply") {
      return [
        "Barber supplies",
        "Cool care",
        "Clipper oil",
        "Replacement blades",
        "Back to shopping",
        "Show related products",
      ];
    }

    return [
      "Lineup setup",
      "Fade setup",
      "Clippers",
      "Trimmers",
      "Shavers",
      "Barber supplies",
      "Lower price",
      "What about Wahl",
    ];
  }

  if (state.flowType === "category") {
    if (state.currentCategory === "trimmer") {
      return [
        "Trimmers",
        "Lower price",
        "Compare top 2",
        "What about Wahl",
        "Replacement blades",
        "Need blade care",
        "Show related products",
      ];
    }
    if (state.currentCategory === "shaver") {
      return [
        "Shavers",
        "Lower price",
        "Compare top 2",
        "What about Wahl",
        "Replacement foil",
        "Need blade care",
        "Show related products",
      ];
    }
    if (state.currentCategory === "clipper") {
      return [
        "Clippers",
        "Lower price",
        "Compare top 2",
        "What about Wahl",
        "Clipper accessories",
        "Need blade care",
        "Show related products",
      ];
    }
    if (state.currentCategory === "supply") {
      return [
        "Barber supplies",
        "Cool care",
        "Clipper oil",
        "Replacement blades",
        "Back to shopping",
        "Show related products",
      ];
    }
  }

  if (state.flowType === "compatibility") {
    return [
      "Blade compatibility",
      "Replacement blades",
      "Replacement foil",
      "Back to shopping",
    ];
  }

  return DEFAULT_SUGGESTIONS;
};

const buildToolQueryForContext = (
  tool: "clipper" | "trimmer" | "foil shaver" | "barber supplies",
  context: FollowupContext
) => {
  const brandPart = context.brand ? `${context.brand} ` : "";
  const useCase = context.useCase || "";

  if (useCase === "beginner") {
    return `best beginner barber ${tool}`.trim();
  }
  if (useCase === "fades") {
    return `${brandPart}${tool} for fades`.trim();
  }
  if (useCase === "lineups") {
    return tool === "foil shaver"
      ? `${brandPart}${tool} for fades`.trim()
      : `${brandPart}${tool} for lineups`.trim();
  }
  if (useCase === "beard") {
    return `${brandPart}${tool} for beard work`.trim();
  }

  return `${brandPart}${tool}`.trim();
};

const buildQueryFromAssistantState = (
  state: AssistantState,
  context: FollowupContext
) => {
  const effectiveUseCase = state.useCase || context.useCase || "";
  const effectiveBrand = state.brand || context.brand || "";
  const brandPart = effectiveBrand ? `${effectiveBrand} ` : "";

  if (state.flowType === "maintenance") {
    if (state.currentCategory === "shaver") {
      return `${brandPart}replacement foil`.trim();
    }
    if (state.currentCategory === "trimmer") {
      return `${brandPart}trimmer blade care`.trim();
    }
    if (state.currentCategory === "clipper") {
      return `${brandPart}clipper blade care`.trim();
    }
    return `${brandPart}blade care`.trim();
  }

  if (state.currentCategory === "clipper") {
    const query = buildToolQueryForContext("clipper", {
      ...context,
      brand: effectiveBrand,
      useCase: effectiveUseCase,
    });
    return state.priceMode === "lower" ? `lower price ${query}` : query;
  }
  if (state.currentCategory === "trimmer") {
    const query = buildToolQueryForContext("trimmer", {
      ...context,
      brand: effectiveBrand,
      useCase: effectiveUseCase,
    });
    return state.priceMode === "lower" ? `lower price ${query}` : query;
  }
  if (state.currentCategory === "shaver") {
    const query = buildToolQueryForContext("foil shaver", {
      ...context,
      brand: effectiveBrand,
      useCase: effectiveUseCase,
    });
    return state.priceMode === "lower" ? `lower price ${query}` : query;
  }
  if (state.currentCategory === "supply") {
    const query = buildToolQueryForContext("barber supplies", {
      ...context,
      brand: effectiveBrand,
      useCase: effectiveUseCase,
    });
    return state.priceMode === "lower" ? `lower price ${query}` : query;
  }
  if (state.flowType === "setup" && effectiveUseCase) {
    if (effectiveUseCase === "starter") {
      return "best beginner barber setup";
    }
    if (effectiveUseCase === "fades") {
      return "best fade setup";
    }
    if (effectiveUseCase === "lineups") {
      return "best lineup setup";
    }
  }

  return context.baseQuery || "";
};

const getAssistantProductTypeLabel = (product: ProductResult) => {
  const classification = getAssistantProductClassification(product.name);
  const family = normalizeText(product.family) || classification.family;
  const rawRole = normalizeText(product.role);
  const role =
    rawRole === "replacement"
      ? "replacement part"
      : rawRole === "support"
      ? "support item"
      : rawRole === "tool"
      ? "complete tool"
      : classification.role === "replacement-part"
      ? "replacement part"
      : classification.role === "support-item"
      ? "support item"
      : classification.role === "tool"
      ? "complete tool"
      : "product";

  return family ? `${family} · ${role}` : role;
};

export const buildAssistantComparison = (
  left: ProductResult,
  right: ProductResult
): AssistantComparison => ({
  leftName: left.name,
  rightName: right.name,
  rows: [
    {
      label: "Price",
      left: formatMoney(left.price, left.currency),
      right: formatMoney(right.price, right.currency),
    },
    {
      label: "Availability",
      left: getAssistantAvailabilityLabel(left),
      right: getAssistantAvailabilityLabel(right),
    },
    {
      label: "SKU",
      left: (left.sku || "Not listed").trim(),
      right: (right.sku || "Not listed").trim(),
    },
    {
      label: "Category",
      left: (left.category || "Not listed").trim(),
      right: (right.category || "Not listed").trim(),
    },
    {
      label: "Product type",
      left: getAssistantProductTypeLabel(left),
      right: getAssistantProductTypeLabel(right),
    },
    {
      label: "Catalog summary",
      left: (left.description || "No catalog summary available.").trim(),
      right: (right.description || "No catalog summary available.").trim(),
    },
  ],
});

const getTagStyle = (tag?: string | null): React.CSSProperties => {
  const label = normalizeText(tag || "");

  if (!label) {
    return {
      background: "#f4f4f4",
      border: "1px solid #e3e3e3",
      color: "#333",
    };
  }
  if (label.includes("best")) {
    return {
      background: "#111",
      border: "1px solid #111",
      color: "#fff",
    };
  }
  if (label.includes("starter") || label.includes("beginner")) {
    return {
      background: "#eefbf1",
      border: "1px solid #cfead7",
      color: "#1f6b3d",
    };
  }
  if (
    label.includes("fade") ||
    label.includes("detail") ||
    label.includes("lineup")
  ) {
    return {
      background: "#f3efff",
      border: "1px solid #ddd1ff",
      color: "#5c3aa3",
    };
  }
  if (label.includes("workhorse") || label.includes("bulk")) {
    return {
      background: "#fff5ea",
      border: "1px solid #f1d9b9",
      color: "#8a4b08",
    };
  }

  return {
    background: "#f7f7f7",
    border: "1px solid #e1e1e1",
    color: "#333",
  };
};

export const buildFollowupContext = (
  query: string,
  previous: FollowupContext,
  results: ProductResult[]
): FollowupContext => {
  const contextualFollowup = isContextualFollowupQuery(query);
  const family =
    inferResultsFamily(results) ||
    (contextualFollowup ? previous.family : "") ||
    "";
  const brand =
    inferBrandFromText(query) ||
    (contextualFollowup ? previous.brand : "") ||
    "";
  const useCase =
    inferUseCaseFromText(query) ||
    (contextualFollowup ? previous.useCase : "") ||
    "";
  return {
    baseQuery: contextualFollowup ? previous.baseQuery || query : query,
    brand,
    family,
    useCase,
  };
};

const getBundleSummary = (lastQuery: string, context: FollowupContext) => {
  const query = normalizeText(lastQuery);
  const useCase = context.useCase || "";

  if (query.includes("fade") || useCase === "fades") {
    return "This setup covers a main cutting tool, detail support, optional close finishing, and basic maintenance for fade-focused work.";
  }
  if (query.includes("lineup") || useCase === "lineups") {
    return "This setup prioritizes outline precision first, then cleanup and maintenance support.";
  }
  if (query.includes("starter") || useCase === "starter") {
    return "This setup is meant to cover the core tools a newer barber would need without overcomplicating the workflow.";
  }
  if (query.includes("beard") || useCase === "beard") {
    return "This setup focuses on shaping, detail work, and maintenance for beard-oriented use.";
  }

  return "This setup covers the main tool, detail support, and maintenance basics.";
};

const scoreForBundleRole = (
  product: ProductResult,
  role: "clipper" | "trimmer" | "shaver" | "support",
  useCase?: string
) => {
  const family = getAssistantResultFamily(product);
  const text = productText(product);

  let score = 0;

  if (role === "clipper") {
    if (family === "clipper") {
      score += 100;
    }
    if (text.includes("replacement")) {
      score -= 100;
    }
  }

  if (role === "trimmer") {
    if (family === "trimmer") {
      score += 100;
    }
    if (family === "blade" || family === "supply") {
      score -= 100;
    }
  }

  if (role === "shaver") {
    if (family === "shaver") {
      score += 100;
    }
    if (text.includes("foil")) {
      score += 20;
    }
  }

  if (role === "support") {
    if (family === "supply") {
      score += 100;
    }
    if (
      text.includes("cool care") ||
      text.includes("oil") ||
      text.includes("barbicide")
    ) {
      score += 20;
    }
  }

  if (useCase === "fades" && text.includes("fade")) {
    score += 20;
  }
  if (useCase === "lineups" && text.includes("lineup")) {
    score += 20;
  }
  if (useCase === "starter" && text.includes("starter")) {
    score += 20;
  }

  return score;
};

const buildSetupBundle = (
  results: ProductResult[],
  context: FollowupContext
) => {
  if (!results.length) {
    return [] as ProductResult[];
  }

  const used = new Set<string>();
  const selected: ProductResult[] = [];

  const pickBest = (role: "clipper" | "trimmer" | "shaver" | "support") => {
    const ranked = results
      .map(product => ({
        product,
        score: scoreForBundleRole(product, role, context.useCase),
      }))
      .filter(entry => entry.score > 0 && !used.has(entry.product.name))
      .sort((left, right) => right.score - left.score);

    if (!ranked.length) {
      return null;
    }

    return ranked[0].product;
  };

  ["clipper", "trimmer", "shaver", "support"].forEach(role => {
    const product = pickBest(
      role as "clipper" | "trimmer" | "shaver" | "support"
    );

    if (!product || used.has(product.name)) {
      return;
    }

    used.add(product.name);
    selected.push(product);
  });

  return selected.slice(0, 4);
};

const getResultRationale = (
  product: ProductResult,
  index: number,
  results: ProductResult[],
  context: FollowupContext,
  lastIntent: string
) => {
  const family = getAssistantResultFamily(product);
  const tag = normalizeText(product.tag || "");
  const useCase = context.useCase || "";
  const price = parsePrice(product.price);
  const priced = results
    .map(item => ({
      name: item.name,
      price: parsePrice(item.price),
    }))
    .filter(item => item.price !== null) as Array<{
    name: string;
    price: number;
  }>;

  const cheapest =
    priced.length > 0
      ? priced.reduce(
          (lowest, item) => (item.price < lowest.price ? item : lowest),
          priced[0]
        )
      : null;

  if (tag.includes("replacement")) {
    return "Shown because it looks like the closest replacement option in this result set.";
  }
  if (tag.includes("blade care")) {
    return "Cataloged as a blade-care match for this request.";
  }
  if (cheapest && price !== null && cheapest.name === product.name) {
    return "This is the lowest listed price in the current result group.";
  }
  if (useCase && tag) {
    return `Included in this guided ${useCase} selection as ${product.tag}.`;
  }
  if (family === "blade") {
    return "Cataloged as an accessory or replacement-part match.";
  }
  if (family === "supply") {
    return "Cataloged as a barber-supply or maintenance match.";
  }
  if (family === "clipper" || family === "shaver" || family === "trimmer") {
    return product.tag
      ? `Selected from the catalog as ${product.tag}.`
      : `Selected as a ${family} match for the current search.`;
  }
  if (lastIntent === "compatibility_search") {
    return "Shown because it appears relevant to the compatibility path you are in.";
  }
  if (index === 0 && tag.includes("best")) {
    return "This appears to be the closest overall match for your current search.";
  }

  return "Shown because it appears relevant to what you are shopping for.";
};

const getHistoryHeading = (entry: ResultHistoryEntry) => {
  const family = entry.followupContext.family || "";
  const brand = entry.followupContext.brand || "";

  if (brand && family) {
    return `${brand} ${family}`;
  }
  if (family) {
    return `${entry.query} · ${family}`;
  }

  return entry.query;
};

const getImageUrl = (product: any) => {
  if (!product) {
    return "";
  }

  if (product.image_url) {
    return product.image_url;
  }
  if (product.imageUrl) {
    return product.imageUrl;
  }
  if (product.thumbnail) {
    return product.thumbnail;
  }
  if (product.image) {
    return product.image;
  }
  if (
    product.images &&
    Array.isArray(product.images) &&
    product.images.length > 0
  ) {
    const first = product.images[0];
    if (typeof first === "string") {
      return first;
    }
    if (first && first.url) {
      return first.url;
    }
  }

  return "";
};

const safeStorefrontUrl = (value: any) => {
  if (typeof value !== "string") {
    return "#";
  }

  const url = value.trim();
  if (
    /^\/(?!\/)/.test(url) ||
    /^https:\/\/www\.egtsupplies\.com\//i.test(url)
  ) {
    return url;
  }

  return "#";
};

const safeCatalogImageUrl = (value: any) => {
  if (typeof value !== "string") {
    return "";
  }

  const url = value.trim();
  if (
    /^\/(?!\/)/.test(url) ||
    /^https:\/\/api\.egtsupplies\.com\/media\//i.test(url) ||
    /^https:\/\/www\.egtsupplies\.com\//i.test(url)
  ) {
    return url;
  }

  return "";
};

export const normalizeAssistantResult = (product: any): ProductResult => {
  const sku =
    product && typeof product.sku === "string" ? product.sku.trim() : "";
  const rawAvailableQuantity = product
    ? product.available_quantity !== undefined
      ? product.available_quantity
      : product.availableQuantity
    : null;
  const parsedAvailableQuantity = Number(rawAvailableQuantity);
  const availableQuantity = Number.isFinite(parsedAvailableQuantity)
    ? parsedAvailableQuantity
    : null;
  const explicitAvailability = product
    ? product.is_available !== undefined
      ? product.is_available
      : product.isAvailable
    : null;

  return {
    available_quantity: availableQuantity,
    category:
      product && typeof product.category === "string"
        ? product.category.trim() || null
        : null,
    currency: product && product.currency ? product.currency : null,
    description:
      product && typeof product.description === "string"
        ? product.description.trim() || null
        : null,
    family:
      product && typeof product.family === "string"
        ? product.family.trim() || null
        : null,
    image_url: safeCatalogImageUrl(getImageUrl(product)) || null,
    is_available:
      typeof explicitAvailability === "boolean"
        ? explicitAvailability
        : availableQuantity === null
        ? null
        : availableQuantity > 0,
    name:
      product && typeof product.name === "string" && product.name.trim()
        ? product.name.trim()
        : "Product",
    price: product && product.price ? product.price : null,
    role:
      product && typeof product.role === "string"
        ? product.role.trim() || null
        : null,
    sku: sku || null,
    tag: product && product.tag ? product.tag : null,
    url: safeStorefrontUrl(product && product.url),
    variant_id: product && product.variant_id ? product.variant_id : null,
  };
};

const isPlainObject = (value: any) =>
  !!value && typeof value === "object" && !Array.isArray(value);

export const restoreAssistantSessionSnapshot = (
  rawValue: string | null,
  now = Date.now()
): AssistantSessionSnapshot | null => {
  if (!rawValue) {
    return null;
  }

  let parsed: any;
  try {
    parsed = JSON.parse(rawValue);
  } catch (_error) {
    return null;
  }

  if (
    !isPlainObject(parsed) ||
    typeof parsed.savedAt !== "number" ||
    parsed.savedAt > now + 5 * 60 * 1000 ||
    now - parsed.savedAt > ASSISTANT_SESSION_MAX_AGE_MS
  ) {
    return null;
  }

  const messages = Array.isArray(parsed.messages)
    ? parsed.messages
        .filter(
          (message: any) =>
            isPlainObject(message) &&
            (message.type === "bot" || message.type === "user") &&
            typeof message.text === "string" &&
            !!message.text.trim()
        )
        .slice(-40)
        .map((message: any) => ({
          text: message.text.trim(),
          type: message.type as "bot" | "user",
        }))
    : [];

  const normalizeStoredResults = (items: any) =>
    Array.isArray(items)
      ? items
          .filter(
            (item: any) =>
              isPlainObject(item) &&
              typeof item.name === "string" &&
              typeof item.url === "string"
          )
          .slice(0, 6)
          .map(normalizeAssistantResult)
      : [];

  const followupContext = isPlainObject(parsed.followupContext)
    ? {
        baseQuery:
          typeof parsed.followupContext.baseQuery === "string"
            ? parsed.followupContext.baseQuery
            : "",
        brand:
          typeof parsed.followupContext.brand === "string"
            ? parsed.followupContext.brand
            : "",
        family:
          typeof parsed.followupContext.family === "string"
            ? parsed.followupContext.family
            : "",
        useCase:
          typeof parsed.followupContext.useCase === "string"
            ? parsed.followupContext.useCase
            : "",
      }
    : EMPTY_CONTEXT;

  const rawState = isPlainObject(parsed.assistantState)
    ? parsed.assistantState
    : {};
  const assistantState: AssistantState = {
    brand: typeof rawState.brand === "string" ? rawState.brand : "",
    currentCategory: [
      "",
      "blade",
      "clipper",
      "shaver",
      "supply",
      "trimmer",
    ].includes(rawState.currentCategory)
      ? rawState.currentCategory
      : "",
    flowType: [
      "",
      "category",
      "compatibility",
      "maintenance",
      "setup",
    ].includes(rawState.flowType)
      ? rawState.flowType
      : "",
    priceMode: rawState.priceMode === "lower" ? "lower" : "",
    useCase: typeof rawState.useCase === "string" ? rawState.useCase : "",
  };

  const resultHistory = Array.isArray(parsed.resultHistory)
    ? parsed.resultHistory
        .filter(
          (entry: any) =>
            isPlainObject(entry) &&
            typeof entry.id === "string" &&
            typeof entry.query === "string"
        )
        .slice(0, 4)
        .map((entry: any) => ({
          followupContext: isPlainObject(entry.followupContext)
            ? {
                baseQuery:
                  typeof entry.followupContext.baseQuery === "string"
                    ? entry.followupContext.baseQuery
                    : "",
                brand:
                  typeof entry.followupContext.brand === "string"
                    ? entry.followupContext.brand
                    : "",
                family:
                  typeof entry.followupContext.family === "string"
                    ? entry.followupContext.family
                    : "",
                useCase:
                  typeof entry.followupContext.useCase === "string"
                    ? entry.followupContext.useCase
                    : "",
              }
            : EMPTY_CONTEXT,
          id: entry.id,
          intent: typeof entry.intent === "string" ? entry.intent : "",
          query: entry.query,
          results: normalizeStoredResults(entry.results),
          suggestions: Array.isArray(entry.suggestions)
            ? entry.suggestions
                .filter((item: any) => typeof item === "string")
                .slice(0, 8)
            : [],
        }))
    : [];

  return {
    assistantState,
    followupContext,
    lastIntent: typeof parsed.lastIntent === "string" ? parsed.lastIntent : "",
    lastQuery: typeof parsed.lastQuery === "string" ? parsed.lastQuery : "",
    messages,
    resultHistory,
    results: normalizeStoredResults(parsed.results),
    savedAt: parsed.savedAt,
    suggestions: Array.isArray(parsed.suggestions)
      ? parsed.suggestions
          .filter((item: any) => typeof item === "string")
          .slice(0, 8)
      : DEFAULT_SUGGESTIONS,
  };
};

export const filterResultsForAssistantState = (
  items: ProductResult[],
  state: AssistantState
) => {
  if (!items.length || !state.currentCategory) {
    return items;
  }

  const getFamily = (item: ProductResult) => getAssistantResultFamily(item);

  if (state.flowType === "compatibility") {
    return items;
  }

  if (state.flowType === "setup") {
    return items;
  }

  if (state.flowType === "maintenance") {
    const maintenanceFiltered = items.filter(item => {
      const family = getFamily(item);

      if (state.currentCategory === "clipper") {
        return family === "blade" || family === "supply";
      }
      if (state.currentCategory === "trimmer") {
        return family === "blade" || family === "supply";
      }
      if (state.currentCategory === "shaver") {
        return family === "blade" || family === "supply" || family === "shaver";
      }

      return family === "blade" || family === "supply";
    });

    return maintenanceFiltered;
  }

  if (state.currentCategory === "supply") {
    const supplyFiltered = items.filter(item => {
      const family = getFamily(item);
      return family === "blade" || family === "supply";
    });

    return supplyFiltered;
  }

  const categoryFiltered = items.filter(
    item => getFamily(item) === state.currentCategory
  );

  return categoryFiltered;
};

const filterResultsByBrandPreference = (
  items: ProductResult[],
  state: AssistantState
) => {
  const brand = normalizeText(state.brand);

  if (!items.length || !brand) {
    return items;
  }

  return items.filter(item => productText(item).includes(brand));
};

const getRelatedResultsForAssistantState = (
  items: ProductResult[],
  state: AssistantState,
  mainResults: ProductResult[]
) => {
  if (
    !items.length ||
    !state.currentCategory ||
    state.flowType === "compatibility"
  ) {
    return [] as ProductResult[];
  }

  const used = new Set(mainResults.map(item => item.name));
  const getFamily = (item: ProductResult) => getAssistantResultFamily(item);

  const related = items.filter(item => {
    if (used.has(item.name)) {
      return false;
    }

    const family = getFamily(item);

    if (state.flowType === "maintenance") {
      return false;
    }

    if (state.currentCategory === "supply") {
      return (
        family === "clipper" || family === "trimmer" || family === "shaver"
      );
    }

    if (
      state.currentCategory === "clipper" ||
      state.currentCategory === "trimmer" ||
      state.currentCategory === "shaver"
    ) {
      return family === "blade" || family === "supply";
    }

    return false;
  });

  return related.slice(0, 3);
};

const QuantityAddButtons: React.FC<{
  addingSetup: boolean;
  addingVariantId: string;
  onAdd: (
    variantId?: string | null,
    quantity?: number,
    productName?: string
  ) => void;
  product: ProductResult;
}> = ({ addingSetup, addingVariantId, onAdd, product }) => {
  const busy = addingSetup || addingVariantId === product.variant_id;
  const disabledForQuantity = (quantity: number) =>
    busy || !canAddAssistantQuantity(product, quantity);

  const buttonStyle = (
    quantity: number,
    primary = false
  ): React.CSSProperties => ({
    background: primary ? "#111" : "#fff",
    border: primary ? "none" : "1px solid #ddd",
    borderRadius: 999,
    color: primary ? "#fff" : "#111",
    cursor: disabledForQuantity(quantity) ? "not-allowed" : "pointer",
    fontSize: 12,
    fontWeight: 800,
    opacity: disabledForQuantity(quantity) ? 0.6 : 1,
    padding: "7px 10px",
  });

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      <button
        disabled={disabledForQuantity(1)}
        onClick={() => onAdd(product.variant_id, 1, product.name)}
        style={buttonStyle(1, true)}
      >
        {!isAssistantResultAvailable(product)
          ? "Out of stock"
          : addingVariantId === product.variant_id
          ? "Adding..."
          : "Add 1"}
      </button>
      <button
        disabled={disabledForQuantity(2)}
        onClick={() => onAdd(product.variant_id, 2, product.name)}
        style={buttonStyle(2)}
      >
        Add 2
      </button>
      <button
        disabled={disabledForQuantity(3)}
        onClick={() => onAdd(product.variant_id, 3, product.name)}
        style={buttonStyle(3)}
      >
        Add 3
      </button>
    </div>
  );
};

const AiAssistant: React.FC = () => {
  const { addItem } = useCart() as any;

  const [addingSetup, setAddingSetup] = React.useState(false);
  const [addingVariantId, setAddingVariantId] = React.useState("");
  const [
    comparison,
    setComparison,
  ] = React.useState<AssistantComparison | null>(null);
  const [enabled, setEnabled] = React.useState(false);
  const [followupContext, setFollowupContext] = React.useState<FollowupContext>(
    EMPTY_CONTEXT
  );
  const [input, setInput] = React.useState("");
  const [lastIntent, setLastIntent] = React.useState("");
  const [lastQuery, setLastQuery] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [messages, setMessages] = React.useState<Message[]>([]);
  const [open, setOpen] = React.useState(false);
  const [postAddActions, setPostAddActions] = React.useState<PostAddActions>({
    itemCount: 0,
    productName: "",
    quantity: 1,
    show: false,
  });
  const [resultHistory, setResultHistory] = React.useState<
    ResultHistoryEntry[]
  >([]);
  const [relatedResults, setRelatedResults] = React.useState<ProductResult[]>(
    []
  );
  const [results, setResults] = React.useState<ProductResult[]>([]);
  const [suggestions, setSuggestions] = React.useState<string[]>(
    DEFAULT_SUGGESTIONS
  );
  const [assistantState, setAssistantState] = React.useState<AssistantState>(
    EMPTY_ASSISTANT_STATE
  );
  const [historyExpanded, setHistoryExpanded] = React.useState(false);
  const [sessionReady, setSessionReady] = React.useState(false);
  const [setupAddProgress, setSetupAddProgress] = React.useState(0);

  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const latestResultsRef = React.useRef<HTMLDivElement | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);

  React.useEffect(() => {
    let restored: AssistantSessionSnapshot | null = null;
    try {
      restored = restoreAssistantSessionSnapshot(
        window.sessionStorage.getItem(ASSISTANT_SESSION_STORAGE_KEY)
      );
    } catch (_error) {
      restored = null;
    }

    if (restored) {
      setAssistantState(restored.assistantState);
      setFollowupContext(restored.followupContext);
      setLastIntent(restored.lastIntent);
      setLastQuery(restored.lastQuery);
      setMessages(restored.messages);
      setResultHistory(restored.resultHistory);
      setResults(restored.results);
      setSuggestions(
        restored.suggestions.length ? restored.suggestions : DEFAULT_SUGGESTIONS
      );
    }

    setEnabled(true);
    setSessionReady(true);
  }, []);

  React.useEffect(() => {
    if (!sessionReady) {
      return;
    }

    const snapshot: AssistantSessionSnapshot = {
      assistantState,
      followupContext,
      lastIntent,
      lastQuery,
      messages: messages.slice(-40),
      resultHistory: resultHistory.slice(0, 4),
      results: results.slice(0, 6),
      savedAt: Date.now(),
      suggestions: suggestions.slice(0, 8),
    };

    try {
      window.sessionStorage.setItem(
        ASSISTANT_SESSION_STORAGE_KEY,
        JSON.stringify(snapshot)
      );
    } catch (_error) {
      // Storage can be disabled; the assistant remains fully usable in memory.
    }
  }, [
    assistantState,
    followupContext,
    lastIntent,
    lastQuery,
    messages,
    resultHistory,
    results,
    sessionReady,
    suggestions,
  ]);

  React.useEffect(() => {
    if (open && inputRef.current && !loading) {
      inputRef.current.focus();
    }
  }, [loading, open]);

  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, results, comparison, postAddActions.show]);

  React.useEffect(() => {
    if (results.length > 0 && latestResultsRef.current) {
      latestResultsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [results]);

  const addBotMessage = (text: string) => {
    setMessages(previous => [
      ...previous,
      {
        text,
        type: "bot",
      },
    ]);
  };

  const clearConversation = () => {
    setComparison(null);
    setFollowupContext(EMPTY_CONTEXT);
    setInput("");
    setLastIntent("");
    setLastQuery("");
    setMessages([]);
    setPostAddActions({
      itemCount: 0,
      productName: "",
      quantity: 1,
      show: false,
    });
    setRelatedResults([]);
    setResultHistory([]);
    setResults([]);
    setSuggestions(getScopedSuggestions(EMPTY_ASSISTANT_STATE));
    setAssistantState(EMPTY_ASSISTANT_STATE);
    setHistoryExpanded(false);
  };

  const clearTransientPanels = () => {
    setComparison(null);
    setRelatedResults([]);
    setPostAddActions({
      itemCount: 0,
      productName: "",
      quantity: 1,
      show: false,
    });
  };

  const closeAssistant = () => {
    setOpen(false);
    window.setTimeout(() => {
      if (triggerRef.current) {
        triggerRef.current.focus();
      }
    }, 0);
  };

  const pushHistorySnapshot = (
    query: string,
    intent: string,
    snapshotContext: FollowupContext,
    snapshotResults: ProductResult[],
    snapshotSuggestions: string[]
  ) => {
    if (!query.trim() || !snapshotResults.length) {
      return;
    }

    setResultHistory(previous => {
      const nextEntry: ResultHistoryEntry = {
        followupContext: snapshotContext,
        id: `${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 8)}`,
        intent,
        query,
        results: snapshotResults.slice(0, 6),
        suggestions: snapshotSuggestions.slice(0, 8),
      };

      const filtered = previous.filter(
        entry =>
          !(
            entry.query === query &&
            JSON.stringify(entry.results) ===
              JSON.stringify(snapshotResults.slice(0, 6))
          )
      );

      return [nextEntry, ...filtered].slice(0, 4);
    });
  };

  const restoreHistoryEntry = (entry: ResultHistoryEntry) => {
    if (!entry.results.length) {
      return;
    }

    clearTransientPanels();
    setComparison(null);
    setFollowupContext(entry.followupContext || EMPTY_CONTEXT);
    setLastIntent(entry.intent);
    setLastQuery(entry.query);
    setRelatedResults([]);
    setResults(entry.results);
    setSuggestions(
      entry.suggestions.length ? entry.suggestions : DEFAULT_SUGGESTIONS
    );
    setHistoryExpanded(false);
    setAssistantState(previous =>
      deriveAssistantState(
        entry.query,
        previous,
        entry.followupContext,
        entry.intent
      )
    );
    addBotMessage(`Restored results for ${entry.query}.`);
  };

  const addVariantToCart = async (
    variantId?: string | null,
    quantity = 1,
    productName?: string
  ) => {
    if (!variantId) {
      addBotMessage(
        "I could not add that item because no purchasable variant was found."
      );
      return;
    }
    if (!addItem) {
      addBotMessage("Cart integration is not available in this session yet.");
      return;
    }

    try {
      setAddingVariantId(variantId);
      await addItem(variantId, quantity);
      setPostAddActions({
        itemCount: 0,
        productName: productName || "Item",
        quantity,
        show: true,
      });
    } catch (error) {
      addBotMessage(
        `I had trouble adding that item to the cart: ${
          error && error.message ? error.message : "unknown error"
        }.`
      );
    } finally {
      setAddingVariantId("");
    }
  };

  const addSetupToCart = async () => {
    const cartItems = getAssistantSetupCartItems(results);

    if (!cartItems) {
      addBotMessage(
        "I could not add the complete setup because one or more items are unavailable or duplicated. You can still add available items individually."
      );
      return;
    }
    if (!addItem) {
      addBotMessage("Cart integration is not available in this session yet.");
      return;
    }

    let addedItemCount = 0;

    try {
      setAddingSetup(true);
      setSetupAddProgress(0);

      for (const item of cartItems) {
        await addItem(item.variantId, 1);
        addedItemCount += 1;
        setSetupAddProgress(addedItemCount);
      }

      setPostAddActions({
        itemCount: addedItemCount,
        productName: "",
        quantity: 1,
        show: true,
      });
      addBotMessage(
        `Added all ${addedItemCount} recommended setup items to your cart. Review quantities before checkout.`
      );
    } catch (error) {
      if (addedItemCount > 0) {
        setPostAddActions({
          itemCount: addedItemCount,
          productName: "",
          quantity: 1,
          show: true,
        });
        addBotMessage(
          `I added ${addedItemCount} of ${cartItems.length} setup items before the cart stopped responding. Review your cart before trying the remaining items.`
        );
      } else {
        addBotMessage(
          `I had trouble adding the setup to the cart: ${
            error && error.message ? error.message : "unknown error"
          }.`
        );
      }
    } finally {
      setAddingSetup(false);
      setSetupAddProgress(0);
    }
  };

  const sendMessage = async (
    preset?: string,
    _preservedSuggestions: string[] = [],
    contextOverride?: FollowupContext,
    stateOverride?: AssistantState
  ) => {
    const text = (preset || input).trim();

    if (!text || loading) {
      return;
    }

    if (lastQuery.trim() && results.length) {
      pushHistorySnapshot(
        lastQuery,
        lastIntent,
        followupContext,
        results,
        suggestions
      );
    }

    setMessages(previous => [
      ...previous,
      {
        text,
        type: "user",
      },
    ]);
    setLoading(true);
    setInput("");
    setHistoryExpanded(false);
    clearTransientPanels();

    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    const requestTimeout = controller
      ? window.setTimeout(
          () => controller.abort(),
          ASSISTANT_REQUEST_TIMEOUT_MS
        )
      : null;

    try {
      const forcedMaintenance = isMaintenanceQuery(text);
      const response = await fetch(API_URL, {
        body: JSON.stringify({ message: text }),
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        signal: controller ? controller.signal : undefined,
      });

      if (!response.ok) {
        throw new Error(
          `Assistant request failed with status ${response.status}`
        );
      }

      const data = (await response.json()) as AiResponse;
      if (!data || typeof data !== "object") {
        throw new Error("Assistant returned an invalid response");
      }
      if (data.ok === false) {
        throw new Error("Assistant could not complete the request");
      }
      const rawResults = Array.isArray(data.results)
        ? data.results.map(normalizeAssistantResult)
        : [];
      const nextIntent = forcedMaintenance
        ? "maintenance_search"
        : data.intent || "";
      const provisionalContext = contextOverride
        ? buildFollowupContext(text, contextOverride, rawResults)
        : buildFollowupContext(text, followupContext, rawResults);
      const stateBeforeRequest = stateOverride || assistantState;
      const derivedState = deriveAssistantState(
        text,
        stateBeforeRequest,
        provisionalContext,
        nextIntent
      );
      const nextState =
        stateOverride && !isCompatibilityQuery(text)
          ? stateOverride
          : derivedState;
      const brandFilteredResults = filterResultsByBrandPreference(
        rawResults,
        nextState
      );
      const nextResults = filterResultsForAssistantState(
        brandFilteredResults,
        nextState
      );
      const nextRelatedResults = getRelatedResultsForAssistantState(
        brandFilteredResults,
        nextState,
        nextResults
      );
      const nextContext = contextOverride
        ? buildFollowupContext(text, contextOverride, nextResults)
        : buildFollowupContext(text, followupContext, nextResults);
      const backendReply =
        data && typeof data.reply === "string" && data.reply.trim()
          ? data.reply.trim()
          : "I couldn't find a response for that request. Please try a product name or SKU.";
      const normalizedReply = normalizeText(backendReply);
      let reply = backendReply;

      if (
        nextState.brand &&
        rawResults.length > 0 &&
        brandFilteredResults.length === 0
      ) {
        reply = `I couldn't find ${nextState.brand} matches for this request. Try another brand or broaden the category.`;
      } else if (
        nextState.flowType === "maintenance" &&
        (normalizedReply.includes("compatibility depends") ||
          normalizedReply.includes("pick your clipper") ||
          normalizedReply.includes("which blade fits"))
      ) {
        reply =
          nextResults.length > 0
            ? "Here are the maintenance and replacement options that match this step."
            : "I couldn't find a maintenance or replacement item for this step. Try Cool care, Clipper oil, or a different tool category.";
      } else if (
        nextState.flowType === "maintenance" &&
        rawResults.length > 0 &&
        nextResults.length === 0
      ) {
        reply =
          "I found related products, but none are maintenance or replacement items for this step. Try Cool care, Clipper oil, or another tool category.";
      } else if (
        nextState.flowType === "category" &&
        nextState.currentCategory !== "supply" &&
        rawResults.length > 0 &&
        nextResults.length === 0
      ) {
        reply = `I found related products, but none are complete ${nextState.currentCategory} tools. Try another brand or broaden the search.`;
      }

      addBotMessage(reply);
      setRelatedResults(nextRelatedResults);
      setResults(nextResults);
      const backendSuggestions = Array.isArray(data.suggestions)
        ? data.suggestions
            .filter(item => typeof item === "string" && item.trim())
            .map(item => item.trim())
        : [];
      setSuggestions(
        Array.from(
          new Set([...backendSuggestions, ...getScopedSuggestions(nextState)])
        ).slice(0, 8)
      );
      setLastIntent(nextIntent);
      setLastQuery(text);
      setFollowupContext(nextContext);
      setAssistantState(nextState);
    } catch (error) {
      const timedOut = error && error.name === "AbortError";
      addBotMessage(
        timedOut
          ? "Product search took too long. Please try again in a moment."
          : "I couldn't reach product search just now. Please try again."
      );
    } finally {
      if (requestTimeout !== null) {
        window.clearTimeout(requestTimeout);
      }
      setLoading(false);
    }
  };

  const applyChipStateTransition = (value: string) => {
    const normalized = normalizeText(value);
    const current = assistantState;
    const brandPart = current.brand ? `${current.brand} ` : "";

    if (normalized === "beginner setup") {
      const nextState: AssistantState = {
        ...current,
        currentCategory: "",
        flowType: "setup",
        useCase: "starter",
      };
      return {
        nextState,
        query: "best beginner barber setup",
      };
    }
    if (normalized === "fade setup") {
      const nextState: AssistantState = {
        ...current,
        currentCategory: "",
        flowType: "setup",
        useCase: "fades",
      };
      return {
        nextState,
        query: "best fade setup",
      };
    }
    if (normalized === "lineup setup") {
      const nextState: AssistantState = {
        ...current,
        currentCategory: "",
        flowType: "setup",
        useCase: "lineups",
      };
      return {
        nextState,
        query: "best lineup setup",
      };
    }
    if (normalized === "clippers" || normalized === "clipper") {
      const nextState: AssistantState = {
        ...current,
        currentCategory: "clipper",
        flowType: "category",
      };
      return {
        nextState,
        query:
          buildQueryFromAssistantState(nextState, followupContext) || "clipper",
      };
    }
    if (normalized === "trimmers" || normalized === "trimmer") {
      const nextState: AssistantState = {
        ...current,
        currentCategory: "trimmer",
        flowType: "category",
      };
      return {
        nextState,
        query:
          buildQueryFromAssistantState(nextState, followupContext) || "trimmer",
      };
    }
    if (normalized === "shavers" || normalized === "shaver") {
      const nextState: AssistantState = {
        ...current,
        currentCategory: "shaver",
        flowType: "category",
      };
      return {
        nextState,
        query:
          buildQueryFromAssistantState(nextState, followupContext) ||
          "foil shaver",
      };
    }
    if (normalized === "barber supplies" || normalized === "shop supplies") {
      const nextState: AssistantState = {
        ...current,
        currentCategory: "supply",
        flowType: "category",
      };
      return {
        nextState,
        query:
          buildQueryFromAssistantState(nextState, followupContext) ||
          "barber supplies",
      };
    }
    if (normalized === "lower price" || normalized === "cheaper") {
      const nextState: AssistantState = {
        ...current,
        flowType: current.flowType || "category",
        priceMode: "lower",
      };
      return {
        nextState,
        query:
          buildQueryFromAssistantState(nextState, followupContext) || value,
      };
    }
    if (normalized === "what about wahl") {
      const nextState: AssistantState = {
        ...current,
        brand: "wahl",
        flowType: current.flowType || "category",
      };
      return {
        nextState,
        query:
          buildQueryFromAssistantState(nextState, followupContext) || "wahl",
      };
    }
    if (normalized === "need blade care" || normalized === "blade care") {
      const nextState: AssistantState = {
        ...current,
        flowType: "maintenance",
      };
      const baseCategory = nextState.currentCategory || "clipper";
      const query =
        baseCategory === "trimmer"
          ? `${brandPart}trimmer blade care`.trim()
          : baseCategory === "shaver"
          ? `${brandPart}replacement foil`.trim()
          : `${brandPart}clipper blade care`.trim();
      return { nextState, query };
    }
    if (normalized === "cool care") {
      const nextState: AssistantState = {
        ...current,
        flowType: "maintenance",
      };
      return { nextState, query: "cool care" };
    }
    if (normalized === "clipper oil") {
      const nextState: AssistantState = {
        ...current,
        flowType: "maintenance",
      };
      return { nextState, query: "clipper oil" };
    }
    if (
      normalized === "replacement blades" ||
      normalized === "replacement blade"
    ) {
      const nextState: AssistantState = {
        ...current,
        currentCategory: current.currentCategory || "clipper",
        flowType: "maintenance",
      };
      const query =
        nextState.currentCategory === "trimmer"
          ? `${brandPart}trimmer replacement blade`.trim()
          : nextState.currentCategory === "shaver"
          ? `${brandPart}replacement foil`.trim()
          : `${brandPart}clipper replacement blade`.trim();
      return { nextState, query };
    }
    if (normalized === "replacement foil") {
      const nextState: AssistantState = {
        ...current,
        currentCategory: "shaver",
        flowType: "maintenance",
      };
      return { nextState, query: `${brandPart}replacement foil`.trim() };
    }
    if (normalized === "clipper accessories") {
      const nextState: AssistantState = {
        ...current,
        currentCategory: "clipper",
        flowType: "maintenance",
      };
      return { nextState, query: "clipper accessories" };
    }
    if (normalized === "trimmer accessories") {
      const nextState: AssistantState = {
        ...current,
        currentCategory: "trimmer",
        flowType: "maintenance",
      };
      return { nextState, query: "trimmer accessories" };
    }
    if (normalized === "shaver accessories") {
      const nextState: AssistantState = {
        ...current,
        currentCategory: "shaver",
        flowType: "maintenance",
      };
      return { nextState, query: "shaver accessories" };
    }
    if (normalized === "back to shopping") {
      const nextState: AssistantState = {
        ...current,
        flowType: current.currentCategory ? "category" : "",
      };
      return { nextState, query: "" };
    }

    return null;
  };

  const applyLocalMaintenanceSelection = (normalized: string) => {
    const currentCategory = (assistantState.currentCategory ||
      inferResultsFamily(results) ||
      "clipper") as AssistantCategory;
    const nextState: AssistantState = {
      ...assistantState,
      currentCategory: currentCategory || assistantState.currentCategory,
      flowType: "maintenance",
    };

    const familyForItem = (item: ProductResult) =>
      getAssistantResultFamily(item);
    let nextResults: ProductResult[] = [];
    let reply = "";

    if (
      normalized === "replacement blades" ||
      normalized === "replacement blade"
    ) {
      nextResults = results.filter(item => familyForItem(item) === "blade");
      reply =
        "Here are the replacement blade options from the current results.";
    } else if (normalized === "replacement foil") {
      nextResults = results.filter(item => {
        const textValue = productText(item);
        return familyForItem(item) === "blade" || textValue.includes("foil");
      });
      reply =
        "Here are the foil or close-finish replacement options from the current results.";
    } else if (
      normalized === "need blade care" ||
      normalized === "blade care" ||
      normalized === "cool care" ||
      normalized === "clipper oil" ||
      normalized === "clipper accessories" ||
      normalized === "trimmer accessories" ||
      normalized === "shaver accessories"
    ) {
      nextResults = results.filter(item => {
        const family = familyForItem(item);
        return family === "blade" || family === "supply";
      });
      reply =
        "Here are the maintenance and accessory items from the current results.";
    }

    nextResults = filterResultsByBrandPreference(nextResults, nextState);

    if (!nextResults.length) {
      return false;
    }

    clearTransientPanels();
    setAssistantState(nextState);
    setRelatedResults([]);
    setResults(nextResults);
    setSuggestions(getScopedSuggestions(nextState));
    addBotMessage(reply);
    return true;
  };

  const handleSuggestionClick = async (value: string) => {
    const normalized = normalizeText(value);

    if (applyLocalMaintenanceSelection(normalized)) {
      return;
    }

    const transition = applyChipStateTransition(value);

    if (normalized === "compare top 2") {
      if (results.length >= 2) {
        setComparison(buildAssistantComparison(results[0], results[1]));
      } else {
        addBotMessage(
          "I need at least two products in the current results to compare them."
        );
      }
      return;
    }

    if (normalized === "show related products") {
      if (relatedResults.length > 0) {
        setResults(previous => [
          ...previous,
          ...relatedResults.filter(
            item => !previous.some(existing => existing.name === item.name)
          ),
        ]);
        addBotMessage("Added a few related products below the main matches.");
      } else if (results.length > 0) {
        addBotMessage(
          "These are already the strongest matches I have for this step."
        );
      }
      return;
    }

    if (normalized === "add first one to cart") {
      if (results.length > 0) {
        if (!isAssistantResultAvailable(results[0])) {
          addBotMessage(
            `${results[0].name} is currently out of stock, so I did not add it to your cart.`
          );
          return;
        }
        await addVariantToCart(results[0].variant_id, 1, results[0].name);
      } else {
        addBotMessage(
          "I need a current result set before I can add the first product."
        );
      }
      return;
    }

    if (normalized === "starter barber setup") {
      if (!results.length) {
        addBotMessage(
          "I need some current results first before I can build a setup."
        );
        return;
      }

      const bundle = buildSetupBundle(results, followupContext);

      if (!bundle.length) {
        addBotMessage(
          "I could not build a useful setup from the current results."
        );
        return;
      }

      setComparison(null);
      setRelatedResults([]);
      setResults(bundle);
      addBotMessage(getBundleSummary(lastQuery, followupContext));
      return;
    }

    if (transition) {
      setAssistantState(transition.nextState);
      if (!transition.query) {
        setSuggestions(getScopedSuggestions(transition.nextState));
        addBotMessage("Back in shopping mode.");
        return;
      }

      await sendMessage(
        transition.query,
        getScopedSuggestions(transition.nextState),
        {
          ...followupContext,
          brand: transition.nextState.brand || followupContext.brand,
          family:
            transition.nextState.currentCategory === "supply"
              ? "supply"
              : transition.nextState.currentCategory || followupContext.family,
          useCase: transition.nextState.useCase || followupContext.useCase,
        },
        transition.nextState
      );
      return;
    }

    await sendMessage(value);
  };

  const handleManualSend = async () => {
    const raw = input.trim();
    if (!raw) {
      return;
    }

    if (applyLocalMaintenanceSelection(normalizeText(raw))) {
      setInput("");
      return;
    }

    const typedTransition = applyChipStateTransition(raw);
    if (typedTransition) {
      setAssistantState(typedTransition.nextState);
      if (!typedTransition.query) {
        setSuggestions(getScopedSuggestions(typedTransition.nextState));
        addBotMessage("Back in shopping mode.");
        return;
      }

      await sendMessage(
        typedTransition.query,
        getScopedSuggestions(typedTransition.nextState),
        {
          ...followupContext,
          brand: typedTransition.nextState.brand || followupContext.brand,
          family:
            typedTransition.nextState.currentCategory === "supply"
              ? "supply"
              : typedTransition.nextState.currentCategory ||
                followupContext.family,
          useCase: typedTransition.nextState.useCase || followupContext.useCase,
        },
        typedTransition.nextState
      );
      return;
    }

    await sendMessage();
  };

  if (!enabled) {
    return null;
  }

  const modeBadge = getModeBadgeFromState(assistantState);
  const setupSummary =
    assistantState.flowType === "setup"
      ? getAssistantSetupSummary(results)
      : null;
  const setupCartItems = setupSummary
    ? getAssistantSetupCartItems(results)
    : null;

  return (
    <>
      <button
        aria-controls="egt-assistant-dialog"
        aria-expanded={open}
        onClick={() => setOpen(previous => !previous)}
        ref={triggerRef}
        style={{
          alignItems: "center",
          background: "#111",
          border: "none",
          borderRadius: 999,
          bottom: 18,
          boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
          color: "#fff",
          cursor: "pointer",
          display: "flex",
          fontSize: 14,
          fontWeight: 800,
          gap: 8,
          padding: "12px 16px",
          position: "fixed",
          right: 18,
          zIndex: 40,
        }}
        type="button"
      >
        <span>Ask EGT</span>
      </button>

      {open && (
        <div
          aria-labelledby="egt-assistant-title"
          aria-modal="false"
          id="egt-assistant-dialog"
          role="dialog"
          style={{
            background: "#f7f7f7",
            border: "1px solid #ddd",
            borderRadius: 18,
            bottom: 92,
            boxShadow: "0 16px 40px rgba(0,0,0,0.18)",
            display: "flex",
            flexDirection: "column",
            height:
              messages.length || results.length || loading
                ? "min(78vh, 760px)"
                : "auto",
            maxHeight: "min(78vh, 760px)",
            maxWidth: 460,
            overflow: "hidden",
            position: "fixed",
            right: 16,
            width: "calc(100% - 32px)",
            zIndex: 50,
          }}
        >
          <div
            style={{
              alignItems: "center",
              background: "#111",
              color: "#fff",
              display: "flex",
              justifyContent: "space-between",
              padding: "14px 16px",
            }}
          >
            <div
              id="egt-assistant-title"
              style={{ fontSize: 15, fontWeight: 800 }}
            >
              Ask EGT
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={clearConversation}
                style={{
                  background: "#fff",
                  border: "none",
                  borderRadius: 999,
                  color: "#111",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 800,
                  padding: "7px 10px",
                }}
                type="button"
              >
                Clear
              </button>
              <button
                aria-label="Close Ask EGT"
                onClick={closeAssistant}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 20,
                  lineHeight: 1,
                  padding: 0,
                }}
                type="button"
              >
                ×
              </button>
            </div>
          </div>

          <div
            aria-live="polite"
            aria-relevant="additions text"
            ref={scrollRef}
            role="log"
            style={{
              display: "flex",
              flex: 1,
              flexDirection: "column",
              overflowY: "auto",
              padding: 14,
            }}
          >
            {modeBadge && (
              <div
                style={{
                  alignSelf: "flex-start",
                  background: "#fff",
                  border: "1px solid #e3e3e3",
                  borderRadius: 999,
                  color: "#333",
                  fontSize: 11,
                  fontWeight: 800,
                  marginBottom: 10,
                  padding: "5px 9px",
                }}
              >
                {modeBadge}
              </div>
            )}

            {messages.map((message, index) => (
              <div
                key={`${message.type}-${index}-${message.text.slice(0, 24)}`}
                style={{
                  alignSelf:
                    message.type === "user" ? "flex-end" : "flex-start",
                  background: message.type === "user" ? "#111" : "#fff",
                  border:
                    message.type === "user" ? "none" : "1px solid #e8e8e8",
                  borderRadius: 14,
                  color: message.type === "user" ? "#fff" : "#111",
                  lineHeight: 1.6,
                  marginBottom: 10,
                  maxWidth: "88%",
                  padding: "11px 13px",
                  whiteSpace: "pre-wrap",
                }}
              >
                {message.text}
              </div>
            ))}

            {loading && (
              <div
                role="status"
                style={{
                  alignSelf: "flex-start",
                  background: "#fff",
                  border: "1px solid #e8e8e8",
                  borderRadius: 14,
                  color: "#555",
                  lineHeight: 1.5,
                  marginBottom: 10,
                  padding: "10px 13px",
                }}
              >
                Searching current catalog…
              </div>
            )}

            {!results.length && !loading && (
              <div style={{ marginTop: 4 }}>
                <div
                  style={{
                    color: "#666",
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    marginBottom: 8,
                    textTransform: "uppercase",
                  }}
                >
                  How can I help?
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {suggestions.slice(0, 6).map(text => (
                    <button
                      key={text}
                      onClick={() => handleSuggestionClick(text)}
                      style={{
                        background: "#fff",
                        border: "1px solid #ddd",
                        borderRadius: 999,
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 700,
                        padding: "8px 10px",
                      }}
                    >
                      {text}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {results.length > 0 && (
              <div
                ref={latestResultsRef}
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                {setupSummary && (
                  <div
                    style={{
                      background: "#f3efff",
                      border: "1px solid #ddd1ff",
                      borderRadius: 14,
                      color: "#281a47",
                      padding: "13px 14px",
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 800 }}>
                      Suggested {setupSummary.itemCount}-item setup ·{" "}
                      {formatMoney(setupSummary.total, setupSummary.currency)}{" "}
                      combined
                    </div>
                    <div
                      style={{ fontSize: 12, lineHeight: 1.5, marginTop: 5 }}
                    >
                      Review each item below and add only what fits your
                      workflow. Taxes and shipping are calculated at checkout.
                    </div>
                    <button
                      disabled={
                        !setupCartItems || addingSetup || !!addingVariantId
                      }
                      onClick={addSetupToCart}
                      style={{
                        background: "#281a47",
                        border: "none",
                        borderRadius: 999,
                        color: "#fff",
                        cursor:
                          !setupCartItems || addingSetup || !!addingVariantId
                            ? "not-allowed"
                            : "pointer",
                        fontSize: 13,
                        fontWeight: 800,
                        marginTop: 10,
                        opacity:
                          !setupCartItems || addingSetup || !!addingVariantId
                            ? 0.6
                            : 1,
                        padding: "9px 12px",
                      }}
                      type="button"
                    >
                      {addingSetup && setupCartItems
                        ? `Adding ${setupAddProgress} of ${setupCartItems.length}...`
                        : "Add complete setup"}
                    </button>
                    {!setupCartItems && (
                      <div
                        style={{
                          color: "#665b7d",
                          fontSize: 11,
                          lineHeight: 1.5,
                          marginTop: 7,
                        }}
                      >
                        Add available items individually because this setup
                        contains an unavailable or duplicated product.
                      </div>
                    )}
                  </div>
                )}
                {results.map((result, index) => (
                  <div
                    key={`${result.variant_id ||
                      result.url ||
                      result.name}-${index}`}
                    style={{
                      background: "#fff",
                      border: "1px solid #e8e8e8",
                      borderRadius: 16,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                      display: "flex",
                      gap: 12,
                      padding: 12,
                    }}
                  >
                    {result.image_url ? (
                      <img
                        alt={result.name}
                        src={result.image_url}
                        style={{
                          background: "#f3f3f3",
                          borderRadius: 12,
                          boxSizing: "border-box",
                          flexShrink: 0,
                          height: 92,
                          mixBlendMode: "multiply",
                          objectFit: "contain",
                          padding: 6,
                          width: 92,
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          alignItems: "center",
                          background: "#f3f3f3",
                          borderRadius: 12,
                          color: "#666",
                          display: "flex",
                          flexShrink: 0,
                          fontSize: 12,
                          height: 92,
                          justifyContent: "center",
                          width: 92,
                        }}
                      >
                        No image
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        flex: 1,
                        flexDirection: "column",
                        gap: 8,
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          color: "#111",
                          fontSize: 14,
                          fontWeight: 800,
                          lineHeight: 1.35,
                        }}
                      >
                        {result.name}
                      </div>

                      {result.sku && (
                        <div style={{ color: "#666", fontSize: 11 }}>
                          SKU {result.sku}
                        </div>
                      )}

                      <div
                        style={{
                          color: isAssistantResultAvailable(result)
                            ? result.available_quantity !== null &&
                              result.available_quantity !== undefined &&
                              result.available_quantity <= 5
                              ? "#8a4b08"
                              : "#1f6b3d"
                            : "#a32626",
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        {getAssistantAvailabilityLabel(result)}
                      </div>

                      {result.tag && (
                        <div
                          style={{ display: "flex", flexWrap: "wrap", gap: 6 }}
                        >
                          <span
                            style={{
                              ...getTagStyle(result.tag),
                              alignSelf: "flex-start",
                              borderRadius: 999,
                              display: "inline-block",
                              fontSize: 11,
                              fontWeight: 800,
                              letterSpacing: 0.2,
                              padding: "5px 9px",
                            }}
                          >
                            {result.tag}
                          </span>
                        </div>
                      )}

                      <div
                        style={{
                          color: "#555",
                          fontSize: 12,
                          lineHeight: 1.5,
                        }}
                      >
                        {getResultRationale(
                          result,
                          index,
                          results,
                          followupContext,
                          lastIntent
                        )}
                      </div>

                      {result.description && (
                        <details
                          style={{
                            background: "#fafafa",
                            border: "1px solid #ececec",
                            borderRadius: 10,
                            color: "#333",
                            fontSize: 12,
                            lineHeight: 1.55,
                            padding: "8px 10px",
                          }}
                        >
                          <summary
                            style={{ cursor: "pointer", fontWeight: 800 }}
                          >
                            Catalog details
                          </summary>
                          <div style={{ marginTop: 7 }}>
                            {result.description}
                          </div>
                        </details>
                      )}

                      <div
                        style={{
                          color: "#111",
                          fontSize: 15,
                          fontWeight: 700,
                        }}
                      >
                        {formatMoney(result.price, result.currency)}
                      </div>

                      <div
                        style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
                      >
                        {result.url !== "#" && (
                          <a
                            href={result.url}
                            rel="noopener noreferrer"
                            style={{
                              alignItems: "center",
                              background: "#fff",
                              border: "1px solid #d9d9d9",
                              borderRadius: 999,
                              color: "#111",
                              display: "inline-flex",
                              fontSize: 13,
                              fontWeight: 700,
                              padding: "8px 10px",
                              textDecoration: "none",
                            }}
                            target="_blank"
                          >
                            View product
                          </a>
                        )}

                        <QuantityAddButtons
                          addingSetup={addingSetup}
                          addingVariantId={addingVariantId}
                          onAdd={addVariantToCart}
                          product={result}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {comparison && (
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e9e9e9",
                  borderRadius: 12,
                  color: "#111",
                  marginTop: results.length ? 12 : 0,
                  padding: "14px 16px",
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 800 }}>
                  Catalog comparison
                </div>
                <div
                  style={{
                    color: "#666",
                    fontSize: 11,
                    lineHeight: 1.5,
                    marginTop: 4,
                  }}
                >
                  Uses current catalog facts and stock—not inferred performance
                  claims.
                </div>
                <div style={{ marginTop: 10, overflowX: "auto" }}>
                  <table
                    style={{
                      borderCollapse: "collapse",
                      fontSize: 11,
                      lineHeight: 1.45,
                      minWidth: 520,
                      tableLayout: "fixed",
                      width: "100%",
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            borderBottom: "1px solid #ddd",
                            padding: "7px 6px",
                            textAlign: "left",
                            width: 90,
                          }}
                        >
                          Fact
                        </th>
                        <th
                          style={{
                            borderBottom: "1px solid #ddd",
                            padding: "7px 6px",
                            textAlign: "left",
                          }}
                        >
                          {comparison.leftName}
                        </th>
                        <th
                          style={{
                            borderBottom: "1px solid #ddd",
                            padding: "7px 6px",
                            textAlign: "left",
                          }}
                        >
                          {comparison.rightName}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.rows.map(row => (
                        <tr key={row.label}>
                          <th
                            scope="row"
                            style={{
                              borderBottom: "1px solid #eee",
                              padding: "7px 6px",
                              textAlign: "left",
                              verticalAlign: "top",
                            }}
                          >
                            {row.label}
                          </th>
                          <td
                            style={{
                              borderBottom: "1px solid #eee",
                              padding: "7px 6px",
                              verticalAlign: "top",
                              wordBreak: "break-word",
                            }}
                          >
                            {row.left}
                          </td>
                          <td
                            style={{
                              borderBottom: "1px solid #eee",
                              padding: "7px 6px",
                              verticalAlign: "top",
                              wordBreak: "break-word",
                            }}
                          >
                            {row.right}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {postAddActions.show && (
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e9e9e9",
                  borderRadius: 12,
                  marginTop: results.length || comparison ? 12 : 0,
                  padding: "14px 16px",
                }}
              >
                <div
                  style={{
                    color: "#111",
                    fontSize: 14,
                    fontWeight: 700,
                    lineHeight: 1.5,
                    marginBottom: 10,
                  }}
                >
                  {postAddActions.itemCount > 0
                    ? `Added ${postAddActions.itemCount} setup items to your cart.`
                    : `Added quantity ${postAddActions.quantity} of ${postAddActions.productName} to your cart.`}
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <a
                    href={CHECKOUT_URL}
                    style={{
                      alignItems: "center",
                      background: "#111",
                      border: "none",
                      borderRadius: 999,
                      color: "#fff",
                      display: "inline-flex",
                      fontSize: 13,
                      fontWeight: 700,
                      padding: "8px 12px",
                      textDecoration: "none",
                    }}
                  >
                    Checkout
                  </a>

                  <button
                    onClick={() =>
                      setPostAddActions({
                        itemCount: 0,
                        productName: "",
                        quantity: 1,
                        show: false,
                      })
                    }
                    style={{
                      background: "#fff",
                      border: "1px solid #ddd",
                      borderRadius: 999,
                      color: "#111",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 700,
                      padding: "8px 12px",
                    }}
                  >
                    Keep shopping
                  </button>
                </div>
              </div>
            )}

            {results.length > 0 && suggestions.length > 0 && (
              <div
                style={{
                  marginTop:
                    results.length || comparison || postAddActions.show
                      ? 14
                      : 0,
                }}
              >
                <div
                  style={{
                    color: "#666",
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    marginBottom: 8,
                    textTransform: "uppercase",
                  }}
                >
                  Try one of these
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {suggestions.map(text => (
                    <button
                      key={text}
                      onClick={() => handleSuggestionClick(text)}
                      style={{
                        background: "#fff",
                        border: "1px solid #ddd",
                        borderRadius: 999,
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 700,
                        padding: "8px 10px",
                      }}
                    >
                      {text}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {relatedResults.length > 0 && (
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e7e7e7",
                  borderRadius: 16,
                  marginTop: 8,
                  padding: 12,
                }}
              >
                <div
                  style={{
                    color: "#666",
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    marginBottom: 10,
                    textTransform: "uppercase",
                  }}
                >
                  Related products
                </div>

                <div
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  {relatedResults.map((result, index) => (
                    <div
                      key={`related-${result.variant_id ||
                        result.url ||
                        result.name}-${index}`}
                      style={{
                        alignItems: "center",
                        border: "1px solid #ececec",
                        borderRadius: 12,
                        display: "flex",
                        gap: 10,
                        padding: 10,
                      }}
                    >
                      {result.image_url ? (
                        <img
                          alt={result.name}
                          src={result.image_url}
                          style={{
                            background: "#f3f3f3",
                            borderRadius: 8,
                            boxSizing: "border-box",
                            flexShrink: 0,
                            height: 52,
                            mixBlendMode: "multiply",
                            objectFit: "contain",
                            padding: 4,
                            width: 52,
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            alignItems: "center",
                            background: "#f3f3f3",
                            borderRadius: 8,
                            color: "#777",
                            display: "flex",
                            flexShrink: 0,
                            fontSize: 10,
                            height: 52,
                            justifyContent: "center",
                            width: 52,
                          }}
                        >
                          No image
                        </div>
                      )}

                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            color: "#111",
                            fontSize: 12,
                            fontWeight: 700,
                            lineHeight: 1.3,
                          }}
                        >
                          {result.name}
                        </div>
                        {result.sku && (
                          <div
                            style={{
                              color: "#777",
                              fontSize: 10,
                              marginTop: 2,
                            }}
                          >
                            SKU {result.sku}
                          </div>
                        )}
                        <div
                          style={{
                            color: isAssistantResultAvailable(result)
                              ? "#1f6b3d"
                              : "#a32626",
                            fontSize: 10,
                            fontWeight: 700,
                            marginTop: 2,
                          }}
                        >
                          {getAssistantAvailabilityLabel(result)}
                        </div>
                        <div
                          style={{
                            color: "#666",
                            fontSize: 11,
                            marginTop: 2,
                          }}
                        >
                          {formatMoney(result.price, result.currency)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {resultHistory.length > 0 && (
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e7e7e7",
                  borderRadius: 16,
                  marginTop: 8,
                  padding: 12,
                }}
              >
                <div
                  style={{
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{
                      color: "#666",
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    Recent result sets
                  </div>

                  {resultHistory.length > 1 && (
                    <button
                      onClick={() => setHistoryExpanded(previous => !previous)}
                      style={{
                        background: "#fff",
                        border: "1px solid #ddd",
                        borderRadius: 999,
                        cursor: "pointer",
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "6px 10px",
                      }}
                    >
                      {historyExpanded ? "Hide history" : "Show history"}
                    </button>
                  )}
                </div>

                {historyExpanded && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    {resultHistory.map(entry => (
                      <div
                        key={entry.id}
                        style={{
                          border: "1px solid #ececec",
                          borderRadius: 12,
                          padding: 10,
                        }}
                      >
                        <div
                          style={{
                            alignItems: "center",
                            display: "flex",
                            gap: 8,
                            justifyContent: "space-between",
                            marginBottom: 8,
                          }}
                        >
                          <div
                            style={{
                              color: "#111",
                              fontSize: 13,
                              fontWeight: 800,
                            }}
                          >
                            {getHistoryHeading(entry)}
                          </div>

                          <button
                            onClick={() => restoreHistoryEntry(entry)}
                            style={{
                              background: "#fff",
                              border: "1px solid #ddd",
                              borderRadius: 999,
                              cursor: "pointer",
                              fontSize: 12,
                              fontWeight: 700,
                              padding: "6px 10px",
                            }}
                          >
                            Restore
                          </button>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                          }}
                        >
                          {entry.results.slice(0, 2).map((item, index) => (
                            <div
                              key={`${entry.id}-${index}`}
                              style={{
                                alignItems: "center",
                                display: "flex",
                                gap: 8,
                              }}
                            >
                              {item.image_url ? (
                                <img
                                  alt={item.name}
                                  src={item.image_url}
                                  style={{
                                    background: "#f3f3f3",
                                    borderRadius: 8,
                                    boxSizing: "border-box",
                                    flexShrink: 0,
                                    height: 44,
                                    mixBlendMode: "multiply",
                                    objectFit: "contain",
                                    padding: 3,
                                    width: 44,
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    alignItems: "center",
                                    background: "#f3f3f3",
                                    borderRadius: 8,
                                    color: "#777",
                                    display: "flex",
                                    flexShrink: 0,
                                    fontSize: 10,
                                    height: 44,
                                    justifyContent: "center",
                                    width: 44,
                                  }}
                                >
                                  No image
                                </div>
                              )}

                              <div style={{ minWidth: 0 }}>
                                <div
                                  style={{
                                    color: "#111",
                                    fontSize: 12,
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {item.name}
                                </div>
                                <div
                                  style={{
                                    color: "#666",
                                    fontSize: 11,
                                    marginTop: 2,
                                  }}
                                >
                                  {formatMoney(item.price, item.currency)}
                                </div>
                              </div>
                            </div>
                          ))}

                          {entry.results.length > 2 && (
                            <div
                              style={{
                                color: "#666",
                                fontSize: 11,
                                marginTop: 2,
                              }}
                            >
                              +{entry.results.length - 2} more items
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div
            style={{
              background: "#fff",
              borderTop: "1px solid #eee",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              padding: 10,
            }}
          >
            <div style={{ display: "flex", gap: 8 }}>
              <input
                aria-label="Ask EGT message"
                autoComplete="off"
                disabled={loading}
                ref={inputRef}
                maxLength={500}
                onChange={event => setInput(event.target.value)}
                onKeyDown={event => {
                  if (event.key === "Enter") {
                    handleManualSend();
                  }
                  if (event.key === "Escape") {
                    closeAssistant();
                  }
                }}
                placeholder="Type what you need..."
                style={{
                  border: "1px solid #ddd",
                  borderRadius: 12,
                  flex: 1,
                  outline: "none",
                  padding: "10px 12px",
                }}
                value={input}
              />
              <button
                disabled={loading}
                onClick={handleManualSend}
                style={{
                  background: "#111",
                  border: "none",
                  borderRadius: 12,
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: 800,
                  opacity: loading ? 0.6 : 1,
                  padding: "10px 14px",
                }}
                type="button"
              >
                {loading ? "Searching…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AiAssistant;
